import React, { useState } from "react";
import CardImg from "../../../components/CardImg";
import Collapse from "../../../components/Collapse";
import { formatBook, formatOdds, formatAmount } from "../../../utils/formatOdds";
import { Link } from "react-router-dom";
import { Modal } from "react-bootstrap";
import CasinoTableLayout from "../../casinoComponents/casinoTableLayout";
import ResultDragonTiger6 from "../resultui/resultDragonTiger6/ResultDragonTiger6";
import { getCasinoRulesHtml } from "../../../data/casinoRulesHtml";
import { IS_REF_MOBILE } from "../../../utils/refViewport";
import { latestBets } from "../../../utils/casinoMyBets";

/**
 * Virtual 1 Day Dragon Tiger (vcasino/dt6, API type vdt6) in the reference markup:
 *   .casino-table.vcasino.vdt1day >
 *     [.progress.mobile-timer (ft > 3)]
 *     .casino-video > .casino-video-title + .video-box-container + .vcasino-cards + right icons (home, rules)
 *     .casino-detail > .main-odds (Dragon | Pair | Tiger .dpt-box)
 *                    + .other-odds (.dragon-section | .dt-divider | .tiger-section: EVEN/ODD/Red/Black + 4 suits)
 *                    + [remark] + .casino-video-last-results
 *   right sidebar: My Bets card only (UserName / Rate / Amount).
 */

const IMG = `${import.meta.env.BASE_URL}img/cards`;
const SUIT_IMG = `${import.meta.env.BASE_URL}img/cardIcons`;
const WINNER_LEFT = `${import.meta.env.BASE_URL}img/winner-left.png`;
const REMARK_ICON = `${import.meta.env.BASE_URL}assets/img/icons/remark.png`;

// Reference v-kvalmin / v-kvalmax: 25 → "25", 25000 → "25K", 100000 → "1L"
function kval(num) {
  const n = Number(num) || 0;
  if (n >= 100000) return `${parseFloat((n / 100000).toFixed(2))}L`;
  if (n >= 1000) return `${parseFloat((n / 1000).toFixed(2))}K`;
  return String(n);
}

const bySr = (a, b) => (Number(a?.sr) || 0) - (Number(b?.sr) || 0);

const FANCY_NAMES = [
  "Pair",
  "Dragon Even", "Dragon Odd", "Dragon Red", "Dragon Black",
  "Dragon Spade", "Dragon Heart", "Dragon Diamond", "Dragon Club",
  "Tiger Even", "Tiger Odd", "Tiger Red", "Tiger Black",
  "Tiger Spade", "Tiger Heart", "Tiger Diamond", "Tiger Club",
];

function formatBetDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
}

// Reference vcasino RightSideBar: the My Bets card only (no rules card)
function VRightSide({ bets = [], onViewMore }) {
  const list = latestBets(bets); // latest 10, like the reference
  return (
    <div className="card m-b-10 my-bet" id={IS_REF_MOBILE ? "casino-bets" : undefined}>
      <div className="card-header">
        <h6 className="card-title float-left">My Bets</h6>{" "}
        <a
          href="javascript:void(0)"
          className="btn btn-back float-right"
          onClick={(e) => {
            e.preventDefault();
            if (typeof onViewMore === "function") onViewMore();
          }}
        >
          View More
        </a>
      </div>{" "}
      <div className="card-body1">
        <div className="tab-content">
          <div id="matched-bet" className="tab-pane active">
            <div className="table-responsive1">
              <table className="table coupon-table table-striped mb-0">
                <thead>
                  <tr>
                    <th style={{ minWidth: "90px" }}>UserName</th>
                    <th className="text-right" style={{ minWidth: "50px" }}>Rate</th>
                    <th className="text-right" style={{ minWidth: "70px" }}>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {list.length === 0 ? (
                    <tr>
                      <td colSpan={4} className="text-center">No records found</td>
                    </tr>
                  ) : (
                    list.map((bet, i) => {
                      const t = String(bet.type || bet.gametype || "").toLowerCase();
                      const border = t === "lay" ? "lay-border" : t === "back" ? "back-border" : undefined;
                      return (
                        <React.Fragment key={bet.id || i}>
                          <tr className={border}>
                            <td colSpan={4}>
                              <b>{bet.selection || bet.matchedBet || "-"}</b>{" "}
                              <span className="float-right">{formatBetDate(bet.placeDate)}</span>
                            </td>
                          </tr>
                          <tr className={border}>
                            <td>{bet.userName}</td>
                            <td className="text-right">{formatOdds(bet.odds)}</td>
                            <td className="text-right">{formatAmount(bet.stake)}</td>
                          </tr>
                          <tr>
                            <td colSpan={4} style={{ height: "3px", padding: 0 }} />
                          </tr>
                        </React.Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function VDt6View({
  gameName = "vdt6",
  title = "V-1 Day Dragon Tiger",
  data,
  exposures = {},
  onBetClick,
  myBets,
  onViewMore,
  viewMoreModal,
}) {
  const [openRanges, setOpenRanges] = useState(() => new Set());
  const [showRules, setShowRules] = useState(false);
  const [showRulesModal, setShowRulesModal] = useState(false);
  const rulesHtml = getCasinoRulesHtml(gameName) || "";

  const sub = Array.isArray(data?.sub) ? data.sub : [];
  const byName = (name) => sub.find((s) => s?.nat === name);
  const mainArr = sub.filter((s) => /^[a-z]*dt6$/.test(String(s?.subtype || ""))).sort(bySr);
  const fancyArr = sub.filter((s) => /^[a-z]*dt6fancy$/.test(String(s?.subtype || ""))).sort(bySr);
  const main = (i) => byName(i === 0 ? "Dragon" : "Tiger") || mainArr[i] || { nat: i === 0 ? "Dragon" : "Tiger" };
  const fancy = (i) => byName(FANCY_NAMES[i]) || fancyArr[i] || { nat: FANCY_NAMES[i] };

  const cards = data?.card ? String(data.card).split(",").map((c) => c.trim()) : [];
  const ft = Number(data?.ft) || 0;
  const lt = Number(data?.lt) || 0;
  const rdesc = String(data?.rdesc ?? "");
  const remark = data?.remark;

  const val = (v) => (v !== undefined && v !== null && v !== "" ? v : 0);
  const getExposure = (name) => {
    const v = exposures?.[name] ?? exposures?.[name?.toLowerCase?.()];
    return v != null ? Number(v) || 0 : 0;
  };
  const bookClass = (v) => (v > 0 ? "book-green" : "book-red");
  const suspended = (item) => String(item?.gstatus || "").toUpperCase() !== "OPEN";
  const click = (item, odd, type) => {
    if (suspended(item)) return;
    onBetClick?.(item?.nat, odd, item, type);
  };

  const toggleRange = (id) =>
    setOpenRanges((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // Dragon / Pair / Tiger boxes
  const dptBox = (cls, id, item, withLay) => {
    const open = openRanges.has(id);
    const exp = getExposure(item?.nat);
    const susp = suspended(item) ? " suspended" : "";
    return (
      <div className={`${cls} dpt-box`}>
        <div>
          <div className="dt-name">
            {`${item?.nat ?? ""}\n                                        `}
            <div className="d-inline-block ml-2">
              <i
                data-toggle="collapse"
                data-target={`#${id}`}
                aria-expanded={open ? "true" : "false"}
                className={`fas fa-info-circle${open ? "" : " collapsed"}`}
                onClick={() => toggleRange(id)}
              />{" "}
              <Collapse id={id} open={open} className="icon-range">
                {"\n                                                R:"}
                <span>{kval(item?.min)}</span>-<span>{kval(item?.max)}</span>
              </Collapse>
            </div>
          </div>{" "}
          <div className="dt-odds">
            <div className={`back-box${susp}`} onClick={() => click(item, val(item?.b), "back")}>
              {val(item?.b)}
            </div>
            {withLay && (
              <>
                {" "}
                <div className={`lay-box${susp}`} onClick={() => click(item, val(item?.l), "lay")}>
                  {val(item?.l)}
                </div>
              </>
            )}
          </div>
        </div>{" "}
        <div className={`dt-book w-100 ${bookClass(exp)}`}>{formatBook(exp)}</div>
      </div>
    );
  };

  // The reference book directive adds .lock-top to the book's suspended parent (the even-box)
  const evenBox = (item, name) => {
    const exp = getExposure(item?.nat);
    return (
      <div
        className={`even-box back-box${suspended(item) ? " suspended lock-top" : ""}`}
        onClick={() => click(item, val(item?.b), "back")}
      >
        <div>
          <div className="dt-name">{name}</div> <div className="dt-odds">{val(item?.b)}</div>
        </div>{" "}
        <div className={`dt-book w-100 ${bookClass(exp)}`}>{formatBook(exp)}</div>
      </div>
    );
  };

  const icons = (...names) =>
    names.map((n, i) => (
      <React.Fragment key={n}>
        {i > 0 && " "}
        <img src={`${SUIT_IMG}/${n}.png`} />
      </React.Fragment>
    ));

  const range = (item) => (
    <div className="w-100 text-right mb-3">
      R:<span>{kval(item?.min)}</span>-<span>{kval(item?.max)}</span>
    </div>
  );

  // off = 1 (Dragon) / 9 (Tiger): Even, Odd, Red, Black, Spade, Heart, Diamond, Club
  const section = (cls, label, playerCls, off) => (
    <div className={cls}>
      <div className={`w-100 ${playerCls} mb-2 text-center`}>{label}</div>{" "}
      {evenBox(fancy(off), "EVEN")} {evenBox(fancy(off + 1), "ODD")}{" "}
      {evenBox(fancy(off + 2), icons("heart", "diamond"))} {evenBox(fancy(off + 3), icons("spade", "club"))}{" "}
      {range(fancy(off))} {evenBox(fancy(off + 4), icons("spade"))} {evenBox(fancy(off + 5), icons("heart"))}{" "}
      {evenBox(fancy(off + 7), icons("club"))} {evenBox(fancy(off + 6), icons("diamond"))} {range(fancy(off + 4))}
    </div>
  );

  const card = (token) => (
    <span>
      <CardImg src={`${IMG}/${token}.png`} />
    </span>
  );

  return (
    <CasinoTableLayout tableClass="vcasino vdt1day" rightSide={<VRightSide bets={myBets} onViewMore={onViewMore} />}>
      <>
        {ft > 3 && (
          <div className="progress mobile-timer">
            <div
              className={`progress-bar ${lt <= 3 ? "bg-danger" : lt <= 6 ? "bg-warning" : "bg-success"}`}
              style={{ width: `${((100 * lt) / ft).toFixed(2)}%` }}
            />
          </div>
        )}{" "}
        <div className="casino-video">
          <div className="casino-video-title">
            <span className="casino-name">{title}</span>{" "}
            <div className="casino-video-rid">Round ID: {data?.mid}</div>
          </div>{" "}
          <div className="video-box-container">
            <div className="video-box" />
          </div>{" "}
          {cards.length > 0 && (
            <div className="vcasino-cards">
              <div className="player-card playera-card">
                {rdesc === "1" && <img src={WINNER_LEFT} className="v-winner" />}{" "}
                <div className="w-100 playera mb-2">Dragon</div> {card(cards[0])}
              </div>{" "}
              <div className="player-card playerb-card">
                {rdesc === "2" && <img src={WINNER_LEFT} className="v-winner" />}{" "}
                <div className="w-100 playerb mb-2">Tiger</div> {card(cards[1] ?? "1")}
              </div>
            </div>
          )}{" "}
          <div className="casino-video-right-icons">
            <div title="Home" className="casino-video-home-icon">
              <Link to="/admin/vcasino/list" style={{ color: "var(--text-highlight)" }}>
                <i className="fas fa-home" />
              </Link>
            </div>{" "}
            <div
              title="Rules"
              className="casino-video-rules-icon"
              onClick={() => {
                setShowRules(true);
                if (IS_REF_MOBILE) setShowRulesModal(true);
              }}
            >
              <i className="fas fa-info-circle" />
            </div>
          </div>{" "}
          <div>
            {showRules && (
              <div
                id="casino-vieo-rules"
                aria-modal="true"
                className="casino-vieo-rules show d-none-small show-rules"
                style={{ display: "block" }}
              >
                <div className="rules-header">
                  <div>Rules</div> <i className="fas fa-times" onClick={() => setShowRules(false)} />
                </div>{" "}
                <div className="rules-body" dangerouslySetInnerHTML={{ __html: rulesHtml }} />
              </div>
            )}
            <Modal
              show={showRulesModal}
              onHide={() => setShowRulesModal(false)}
              className="casino-rules teenpatti2-rule-modal"
              dialogClassName="modal-md"
              id="rules-modal"
            >
              <header className="modal-header">
                <h5 className="modal-title">Rules</h5>{" "}
                <button type="button" aria-label="Close" className="close" onClick={() => setShowRulesModal(false)}>
                  ×
                </button>
              </header>
              <Modal.Body>
                <div className="casino-vieo-rules show-rules show">
                  <div className="rules-body" dangerouslySetInnerHTML={{ __html: rulesHtml }} />
                </div>
              </Modal.Body>
            </Modal>
          </div>
        </div>{" "}
        {data?.mid ? (
          <div className="casino-detail">
            <div className="main-odds">
              {dptBox("dragon-box", "demo0", main(0), true)}{" "}
              {dptBox("pair-box", "demof0", fancy(0), false)}{" "}
              {dptBox("tiger-box", "demo1", main(1), true)}
            </div>{" "}
            <div className="other-odds">
              {section("dragon-section", "DRAGON", "playera", 1)} <div className="dt-divider" />{" "}
              {section("tiger-section", "TIGER", "playerb", 9)}
            </div>{" "}
            {remark ? (
              <div className="casino-remark mt-3">
                <div className="remark-icon">
                  <img src={REMARK_ICON} />
                </div>{" "}
                {React.createElement("marquee", null, remark)}
              </div>
            ) : null}{" "}
            <div className="casino-video-last-results">
              <ResultDragonTiger6 gameType={gameName} />
            </div>
          </div>
        ) : null}
      </>
      {viewMoreModal}
    </CasinoTableLayout>
  );
}
